import { AxiosResponse } from 'axios';
import {
  demoAddresses,
  demoCatchPhrases,
  demoCompanyNames,
  demoModeEmails,
  demoPersonFirstNames,
  demoPersonLastNames,
  demoPersonNames,
  demoPhoneNumbers,
  demoPrices,
  demoUrls,
  randomItemIn,
} from 'features/demo-mode/helpers';
import {
  IdentifiableFieldMetadata,
  IdentifiableFieldTypes,
  IdentifiableType,
} from 'features/demo-mode/types';

const IDENTIFIABLE_FIELD_NAME = '__identifiableFields' as const;

export function toDemoModeResponse(response: AxiosResponse) {
  const { data, ...props } = response;

  const newData = toDemoModeData(data);

  return {
    ...props,
    data: newData,
  };
}

export function toDemoModeData(
  data: Record<string, unknown> | Record<string, unknown>[] | null
): unknown {
  if (data == null || typeof data !== 'object') {
    return data;
  }
  if (Array.isArray(data)) {
    return data.map((element) => toDemoModeData(element));
  }

  // From here on out, data is a non-nullish object
  const hasIdentifiableFields = Object.hasOwn(data, IDENTIFIABLE_FIELD_NAME);
  if (hasIdentifiableFields) {
    const identifiableFieldMetadatas = data[
      IDENTIFIABLE_FIELD_NAME
    ] as IdentifiableFieldMetadata[];

    identifiableFieldMetadatas.forEach((metadata) => {
      data[metadata.fieldName] = toDemoField(metadata);
    });
  }

  // Recursively process rest of object tree to look for identifiable fields
  for (const dataKey in data) {
    const value = data[dataKey];

    if (typeof value === 'object') {
      data[dataKey] = toDemoModeData(value as Record<string, unknown>);
    }
  }

  return data;
}

function toDemoField(fieldMetadata: IdentifiableFieldMetadata) {
  switch (fieldMetadata.type) {
    case IdentifiableFieldTypes.Address:
      return randomItemIn(demoAddresses);
    case IdentifiableFieldTypes.ClientName:
    case IdentifiableFieldTypes.AdvertiserName:
    case IdentifiableFieldTypes.CompanyName:
      return randomItemIn(demoCompanyNames);
    case IdentifiableFieldTypes.CampaignName:
      return randomItemIn(demoCatchPhrases);
    case IdentifiableFieldTypes.Dollar:
      return randomItemIn(demoPrices);
    case IdentifiableFieldTypes.PersonName:
      return randomItemIn(demoPersonNames);
    case IdentifiableFieldTypes.PersonFirstName:
      return randomItemIn(demoPersonFirstNames);
    case IdentifiableFieldTypes.PersonLastName:
      return randomItemIn(demoPersonLastNames);
    case IdentifiableFieldTypes.Email:
      return randomItemIn(demoModeEmails);
    case IdentifiableFieldTypes.PhoneNumber:
      return randomItemIn(demoPhoneNumbers);
    case IdentifiableFieldTypes.Website:
      return randomItemIn(demoUrls);
    case IdentifiableFieldTypes.Generic:
    default:
      return 'REDACTED';
  }
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const chr = value.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

function hashAnonymnize(originalValue: unknown, list: string[]) {
  if (originalValue == null) {
    return list[0];
  }

  const originalString = String(originalValue);
  const hash = hashString(originalString);
  const index = hash % list.length;

  return list[index];
}

export function toDemoValue(
  originalValue: unknown,
  identifiableType: IdentifiableType
): string {
  switch (identifiableType) {
    case IdentifiableFieldTypes.Address:
      return hashAnonymnize(originalValue, demoAddresses);
    case IdentifiableFieldTypes.ClientName:
    case IdentifiableFieldTypes.AdvertiserName:
    case IdentifiableFieldTypes.CompanyName:
      return hashAnonymnize(originalValue, demoCompanyNames);
    case IdentifiableFieldTypes.CampaignName:
      return hashAnonymnize(originalValue, demoCatchPhrases);
    case IdentifiableFieldTypes.Dollar:
      return hashAnonymnize(originalValue, demoPrices);
    case IdentifiableFieldTypes.PersonName:
      return hashAnonymnize(originalValue, demoPersonNames);
    case IdentifiableFieldTypes.PersonFirstName:
      return hashAnonymnize(originalValue, demoPersonFirstNames);
    case IdentifiableFieldTypes.PersonLastName:
      return hashAnonymnize(originalValue, demoPersonLastNames);
    case IdentifiableFieldTypes.Email:
      return hashAnonymnize(originalValue, demoModeEmails);
    case IdentifiableFieldTypes.PhoneNumber:
      return hashAnonymnize(originalValue, demoPhoneNumbers);
    case IdentifiableFieldTypes.Website:
      return hashAnonymnize(originalValue, demoUrls);
    case IdentifiableFieldTypes.Generic:
    default:
      return 'REDACTED';
  }
}
